import React, { forwardRef, MouseEventHandler } from "react";
import { WithTesting } from "../../types";
import useOverflow from "../../hooks/useOverflow";
import { Icon } from "../Icon";
import { StyledBigButton, StyledLabel } from "./BigButton.styled";
import Tooltip from "../Tooltip";

export interface BigButtonProps {
  /**
   * The name of the icon.
   */
  name: string;
  /**
   * Button text (below icon).
   */
  label?: string;
  /**
   * Show button in selected state.
   */
  isSelected?: boolean;
  /**
   * Fill icon, if true - shows painted over icon.
   */
  isFill?: boolean;
  /**
   * If true - BigButton will take all the available space of the container.
   */
  isFullWidth?: boolean;
  /**
   * Horizontally aligns content inside BigButton (icon and label).
   */
  alignment?: "center" | "start" | "end";
  /**
   * Disabled button state.
   */
  disabled?: boolean;
  /**
   * Click handler
   */
  onClick?: MouseEventHandler<HTMLButtonElement>;
  /**
   * Wrap mode is used to tell how BigButton should work with long label, if the mode is `wrap` -
   * it wraps text on a new line, if it's `ellipsis` - adds three dots in the end.
   */
  wrapMode?: "wrap" | "ellipsis";
}

const BigButton = forwardRef<HTMLButtonElement, BigButtonProps & WithTesting>(
  (
    {
      label,
      name,
      isFill,
      isFullWidth,
      isSelected,
      dataTestId,
      alignment,
      disabled,
      onClick,
      wrapMode = "ellipsis",
    },
    ref,
  ) => {
    const { isOverflowing, ref: labelRef } = useOverflow();

    return (
      <StyledBigButton
        isSelected={isSelected}
        isFullWidth={isFullWidth}
        disabled={disabled}
        alignment={alignment}
        onClick={onClick}
        ref={ref}
        data-testid={dataTestId}
      >
        <Icon name={name} size="big" isFill={isFill} />
        <Tooltip title={isOverflowing ? label : ""}>
          <StyledLabel wrapMode={wrapMode} ref={labelRef}>
            {label}
          </StyledLabel>
        </Tooltip>
      </StyledBigButton>
    );
  },
);

export default BigButton;
